import React from "react";
import {
  Palette, Bookmark, Image as ImageIcon, Type as TypeIcon, Square, Circle as CircleIcon,
  Minus, MoveRight, ArrowLeftRight, Layers, Wand2, Trash2, Upload,
  ChevronUp, ChevronDown, Eye, EyeOff, Lock, Unlock, FileText, Droplet, Sun,
} from "lucide-react";
import ColorPicker, { pushRecentColor } from "../ColorPicker";
import SeriesPalettes from "../SeriesPalettes";
import { ToolSection, IconBtn, BackBlurbSection } from "./Chrome";
import { textBtnCls, shapeBtnCls } from "./constants";

const LeftToolbar = ({
  panel,
  bgColor,
  setBgColor,
  panelBg,
  spineMatchFront,
  setSpineMatchFront,
  onSampleSpineFromFront,
  sampling,
  eyedropping,
  onToggleEyedropper,
  backOverlay,
  setBackOverlay,
  onApplyPalette,
  onPlaceBlurb,
  onOpenBlurbModal,
  onUpload,
  onOpenAI,
  onAddText,
  onAddShape,
  layers,
  activeObj,
  onSelectLayer,
  onMoveLayer,
  onToggleVisible,
  onToggleLock,
  onRemoveLayer,
  spineZoom = 3,
  onSpineZoom,
}) => {
  const swatches = [
    "#FFFFFF", "#FAFAF9", "#F1EFE8", "#534AB7", "#3C3489", "#2C2C2A",
    "#1D9E75", "#BA7517", "#D74343", "#3460D7", "#E4D6BA", "#F4A261",
  ];

  // Shared Images section — rendered at the top on back panel, normal position elsewhere
  const ImagesSection = (
    <ToolSection icon={ImageIcon} title="Images">
      <label className="flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8] cursor-pointer">
        <Upload className="w-3.5 h-3.5" /> Upload image
        <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="upload-image-input" />
      </label>
      <button
        onClick={onOpenAI}
        data-testid="open-ai-panel-btn"
        className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489]"
      >
        <Wand2 className="w-3.5 h-3.5" /> Generate with AI
      </button>
    </ToolSection>
  );

  return (
    <aside className="bg-white border-r border-[#D3D1C7] overflow-y-auto" data-testid="left-toolbar">

      {panel === "spine" && onSpineZoom && (
        <div className="px-3 py-2 border-b border-[#D3D1C7]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-[#666]">Spine Zoom</span>
            <span className="text-xs text-[#666]">{spineZoom}x</span>
          </div>
          <input
            type="range"
            min="1"
            max="10"
            step="0.5"
            value={spineZoom}
            onChange={e => onSpineZoom(parseFloat(e.target.value))}
            className="w-full h-1 accent-[#5C4BCC] cursor-pointer"
          />
        </div>
      )}


      {/* Background — always first */}
      <ToolSection icon={Palette} title="Background">
        {panel === "spine" && (
          <>
            <label
              className="flex items-center gap-2 mb-2 px-2 py-2 rounded-md bg-[#F1EFE8]/70 border border-[#D3D1C7] cursor-pointer"
              data-testid="spine-match-toggle"
            >
              <input
                type="checkbox"
                checked={spineMatchFront}
                onChange={(e) => setSpineMatchFront(e.target.checked)}
                data-testid="spine-match-checkbox"
                className="rounded border-[#D3D1C7] accent-[#534AB7]"
              />
              <span className="text-xs text-[#2C2C2A] flex-1">Match front cover colour</span>
              <span className="inline-block w-4 h-4 rounded border border-[#D3D1C7]" style={{ backgroundColor: panelBg.front }} />
            </label>
            <button
              onClick={onSampleSpineFromFront}
              disabled={sampling}
              data-testid="spine-sample-front-btn"
              className="w-full mb-3 flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] border border-[#D3D1C7] text-xs text-[#2C2C2A] hover:bg-[#F1EFE8] disabled:opacity-50"
              title="Sample the dominant colour from your front cover and apply it as a clean solid spine fill"
            >
              <Droplet className="w-3.5 h-3.5 text-[#534AB7]" />
              {sampling ? "Sampling…" : "Sample from front cover"}
            </button>
          </>
        )}

        <ColorPicker
          value={bgColor}
          onChange={(v) => { setBgColor(v); pushRecentColor(v); }}
          testid="bg-color-input"
        />
        <button
          onClick={onToggleEyedropper}
          data-testid="bg-eyedropper-btn"
          className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] border text-xs transition-colors ${
            eyedropping
              ? "bg-[#534AB7] text-white border-[#534AB7]"
              : "border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8]"
          }`}
          title="Click any pixel on the cover to match the background colour to it"
        >
          <Droplet className="w-3.5 h-3.5" />
          {eyedropping ? "Click a pixel on the cover…" : "Pick from cover (eyedropper)"}
        </button>
        <div className="mt-3 grid grid-cols-6 gap-1.5">
          {swatches.map((c) => (
            <button
              key={c}
              onClick={() => { setBgColor(c); pushRecentColor(c); }}
              style={{ background: c }}
              className={`w-8 h-8 rounded-md border ${bgColor === c ? "ring-2 ring-[#534AB7]" : "border-[#D3D1C7]"}`}
              aria-label={`Background ${c}`}
            />
          ))}
        </div>
      </ToolSection>

      {/* On the back panel: Images floats up here — right after Background */}
      {panel === "back" && ImagesSection}

      {panel === "back" && (
        <ToolSection icon={Sun} title="Back-cover overlay">
          <p className="text-[11px] text-[#5F5E5A] leading-snug mb-2">
            Add a translucent dark overlay above your background image so the blurb stays legible.
          </p>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="range"
              min="0"
              max="0.7"
              step="0.05"
              value={backOverlay.opacity}
              onChange={(e) => setBackOverlay({ ...backOverlay, opacity: parseFloat(e.target.value) })}
              data-testid="back-overlay-slider"
              className="flex-1"
            />
            <span className="text-xs tabular-nums w-10 text-right text-[#5F5E5A]">
              {Math.round((backOverlay.opacity || 0) * 100)}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A]">Tint</span>
            <div className="flex-1">
              <ColorPicker
                value={backOverlay.color}
                onChange={(v) => setBackOverlay({ ...backOverlay, color: v })}
                testid="back-overlay-color"
              />
            </div>
          </div>
          {backOverlay.opacity > 0 && (
            <button
              onClick={() => setBackOverlay({ ...backOverlay, opacity: 0 })}
              data-testid="back-overlay-remove-btn"
              className="mt-2 w-full text-[11px] text-[#5F5E5A] underline hover:text-[#D74343]"
            >
              Remove overlay
            </button>
          )}
        </ToolSection>
      )}

      <ToolSection icon={Bookmark} title="Series colours">
        <SeriesPalettes currentBg={bgColor} onApply={onApplyPalette} />
      </ToolSection>

      {panel === "back" && (
        <BackBlurbSection onPlace={onPlaceBlurb} onOpenModal={onOpenBlurbModal} />
      )}

      {/* On front/spine panels: Images appears here in normal position */}
      {panel !== "back" && ImagesSection}

      <ToolSection icon={TypeIcon} title="Text blocks">
        <div className="space-y-1.5">
          <button onClick={() => onAddText("title")} className={textBtnCls} data-testid="add-title-btn">+ Title block</button>
          <button onClick={() => onAddText("subtitle")} className={textBtnCls}>+ Subtitle block</button>
          <button onClick={() => onAddText("author")} className={textBtnCls}>+ Author block</button>
          <button onClick={() => onAddText("custom")} className={textBtnCls}>+ Custom text</button>
        </div>
        <p className="text-[11px] text-[#5F5E5A] mt-2 leading-snug">
          Leave blank if your uploaded image already contains text.
        </p>
      </ToolSection>

      {panel === "back" && (
        <ToolSection icon={FileText} title="Advanced blurb import" defaultOpen={false}>
          <button
            onClick={onOpenBlurbModal}
            data-testid="open-blurb-modal-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8]"
          >
            <FileText className="w-3.5 h-3.5" /> Paste from Word / Google Docs
          </button>
          <p className="text-[11px] text-[#5F5E5A] mt-2 leading-snug">
            Strips HTML automatically and lets you preview before placing.
          </p>
        </ToolSection>
      )}

      <ToolSection icon={Square} title="Shapes">
        <div className="grid grid-cols-3 gap-1.5">
          <button onClick={() => onAddShape("rect")} className={shapeBtnCls} title="Rectangle" data-testid="add-rect-btn"><Square className="w-4 h-4 mx-auto" /></button>
          <button onClick={() => onAddShape("circle")} className={shapeBtnCls} title="Circle" data-testid="add-circle-btn"><CircleIcon className="w-4 h-4 mx-auto" /></button>
          <button onClick={() => onAddShape("line")} className={shapeBtnCls} title="Line" data-testid="add-line-btn"><Minus className="w-4 h-4 mx-auto" /></button>
          <button onClick={() => onAddShape("arrow")} className={shapeBtnCls} title="Tapered arrow" data-testid="add-arrow-btn"><MoveRight className="w-4 h-4 mx-auto" /></button>
          <button onClick={() => onAddShape("double-arrow")} className={shapeBtnCls} title="Two-headed arrow" data-testid="add-double-arrow-btn"><ArrowLeftRight className="w-4 h-4 mx-auto" /></button>
        </div>
      </ToolSection>

      <ToolSection icon={Layers} title="Layers" defaultOpen>
        <div className="space-y-1 max-h-[260px] overflow-y-auto" data-testid="layers-list">
          {layers.length === 0 && (
            <p className="text-xs text-[#5F5E5A] italic">No layers yet — add a background, image or text.</p>
          )}
          {layers.slice().reverse().map((l) => {
            const realIdx = layers.length - 1 - l.idx;
            const isActive = activeObj === l.obj;
            return (
              <div key={realIdx} className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${isActive ? "bg-[#F1EFE8]" : "hover:bg-[#FAFAF9]"}`}>
                <button
                  onClick={() => onSelectLayer(l.obj)}
                  className="text-xs text-[#2C2C2A] flex-1 text-left truncate"
                >
                  {l.label}
                </button>
                <IconBtn icon={l.visible ? Eye : EyeOff} onClick={() => onToggleVisible(l.obj)} title={l.visible ? "Hide" : "Show"} />
                <IconBtn icon={l.locked ? Lock : Unlock} onClick={() => onToggleLock(l.obj)} title={l.locked ? "Unlock" : "Lock"} />
                <IconBtn icon={ChevronUp} onClick={() => onMoveLayer(realIdx, 1)} title="Move up" />
                <IconBtn icon={ChevronDown} onClick={() => onMoveLayer(realIdx, -1)} title="Move down" />
                <IconBtn icon={Trash2} onClick={() => onRemoveLayer(l.obj)} title="Delete" danger />
              </div>
            );
          })}
        </div>
      </ToolSection>

    </aside>
  );
};

export default LeftToolbar;
